import React from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import Draggable from 'react-draggable';
import { Close, OpenInFull } from '@mui/icons-material';
import { useClinicalSession } from '../../contexts/ClinicalSessionContext';

const MinimizedSessionsBar = () => {
  const {
    getMinimizedSessions,
    maximizeSession,
    closeSession,
    chipPositions,
    setChipPosition,
  } = useClinicalSession();

  const minimizedSessions = getMinimizedSessions();
  const [dragging, setDragging] = React.useState({});
  const [localPositions, setLocalPositions] = React.useState({});

  // Default position: bottom-left corner with some padding
  const defaultPosition = { x: 20, y: window.innerHeight - 80 };

  if (minimizedSessions.length === 0) {
    return null;
  }

  const formatName = (name) => {
    if (!name) return 'Unnamed Patient';
    return name;
  };

  return (
    <>
      {minimizedSessions.map((session, index) => {
        // Use stored position or default position (with offset for multiple chips)
        const storedPos = chipPositions[session.id];
        const localPos = localPositions[session.id];
        const currentPosition = localPos ||
          storedPos || {
            x: defaultPosition.x,
            y: defaultPosition.y - index * 50, // Stack chips vertically if multiple
          };

        const chipNode = (
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{formatName(session.sessionData.userName)}</span>
                <IconButton
                  size='small'
                  className='no-drag'
                  onClick={(e) => {
                    e.stopPropagation();
                    maximizeSession(session.id);
                  }}
                  sx={{
                    p: 0.5,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <OpenInFull sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size='small'
                  className='no-drag'
                  onClick={(e) => {
                    e.stopPropagation();
                    closeSession(session.id);
                  }}
                  sx={{
                    p: 0.5,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            }
            sx={{
              backgroundColor: '#9291ef',
              color: 'white',
              fontFamily: 'Lato, sans-serif',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'move',
              minWidth: '200px',
              height: '40px',
              '&:hover': {
                backgroundColor: '#7a79d1',
              },
              '& .MuiChip-label': {
                px: 2,
              },
            }}
          />
        );

        return (
          <Draggable
            key={session.id}
            position={currentPosition}
            cancel='.no-drag'
            onStart={() => {
              setDragging((prev) => ({ ...prev, [session.id]: true }));
            }}
            onDrag={(_, data) => {
              // Update local position for smooth dragging without context updates
              setLocalPositions((prev) => ({
                ...prev,
                [session.id]: { x: data.x, y: data.y },
              }));
            }}
            onStop={(_, data) => {
              // Save to context for persistence
              setChipPosition(session.id, { x: data.x, y: data.y });
              // Clear local position
              setLocalPositions((prev) => {
                const newPos = { ...prev };
                delete newPos[session.id];
                return newPos;
              });
              setTimeout(() => {
                setDragging((prev) => ({ ...prev, [session.id]: false }));
              }, 100);
            }}
          >
            <Box
              sx={{
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              {chipNode}
            </Box>
          </Draggable>
        );
      })}
    </>
  );
};

export default MinimizedSessionsBar;
